// src/components/layout/OfflineStatusBanner.jsx
//
// Global status bar. Three visible states:
//   • navigator.onLine === false          → amber "You're offline — N queued"
//   • online + pending queue > 0          → navy "N syncing"
//   • online + failed queue > 0           → red "N failed — click to retry"
//   • online + queue empty                → nothing rendered
//
// Mounts once in Layout.jsx (+ MobileLayout). Sits below the top of the
// viewport as a slim strip. Doesn't push content — uses fixed positioning
// so the inbox + every page sees the same indicator without reflow.
//
// Stage 3 mutation-queue landed earlier; this is the UX surface that makes
// it visible. Prior to this, users had no signal that a send/archive got
// queued instead of executing.

import { useEffect, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { useMutationQueueStatus } from '../../hooks/useMutationQueueStatus'
import { retryAllFailed } from '../../lib/mutationQueue'

export default function OfflineStatusBanner() {
  const { failed } = useMutationQueueStatus()

  // With PGlite local cache, reads always work and writes queue automatically —
  // the user is never blocked by connectivity. Only surface failed mutations,
  // which are genuinely actionable. Silence all other states.
  if (failed === 0) return null

  const variant = 'red'
  const Icon = AlertCircle
  const label = `${failed} action${failed !== 1 ? 's' : ''} failed to sync — click to retry`
  const onClick = () => retryAllFailed()
  const title = 'Retry all failed actions'

  const styles = {
    amber: 'bg-amber-50 border-amber-300 text-amber-900',
    red:   'bg-red-50 border-red-300 text-red-800',
    navy:  'bg-navy-50 border-navy-200 text-navy-800',
  }[variant]

  const content = (
    <div className="flex items-center justify-center gap-2 text-xs font-medium py-1.5 px-4">
      <Icon size={13} className={variant === 'navy' ? 'animate-spin' : ''} />
      <span>{label}</span>
    </div>
  )

  // In-flow layout: the parent is a flex column, so this bar pushes content
  // below it rather than overlapping a per-page TopBar. Use w-full + border-b
  // to mimic an app-shell strip.
  const commonCls = `w-full border-b ${styles}`
  if (onClick) {
    return (
      <button onClick={onClick} title={title} className={`${commonCls} hover:brightness-95 transition`}>
        {content}
      </button>
    )
  }
  return <div className={commonCls}>{content}</div>
}
